import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {UserInfo as KeycloakUserInfo} from '@/services/keycloak';
import {MatrixCredentials} from '@/types/matrix';
import {PaymentFeeData} from '@/types/payments';

export interface AuthState {
  isAuthenticated: boolean;
  keycloakUser: KeycloakUserInfo | null;
  keycloakReady: boolean;
  isLoading: boolean;
  userBlacklist: string[];
  notificationPermission: NotificationPermission;
  matrixCredentials?: MatrixCredentials | null;
  locationSet: boolean;
  stripeAccountVerified: boolean;
  stripeAccountRequirements: {
    currently_due: string[];
    pending_verification: string[];
    past_due: string[];
  } | null;
  appTag: string | null;
  currentTab: number;
  isUserHealthManager: boolean;
  currentCompanyId: string | null;
  isPublicCompany: boolean;
  currentEvent: string | null;
  currentGroup: string | null;
  currentOnDemandEvent: string | null;
  paymentFeeData: PaymentFeeData | null;
  uses19Vat: boolean | null;
  companySportsLoaded: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  keycloakUser: null,
  keycloakReady: false,
  isLoading: true,
  userBlacklist: [],
  notificationPermission: 'default',
  locationSet: false,
  matrixCredentials: null,
  stripeAccountVerified: false,
  stripeAccountRequirements: {
    currently_due: [],
    pending_verification: [],
    past_due: []
  },
  appTag: null,
  currentTab: 0,
  isUserHealthManager: false,
  currentCompanyId: null,
  isPublicCompany: false,
  currentEvent: null,
  currentOnDemandEvent: null,
  currentGroup: null,
  paymentFeeData: null,
  uses19Vat: null,
  companySportsLoaded: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginUser(state, action: PayloadAction<KeycloakUserInfo>) {
      return {
        ...state,
        isAuthenticated: true,
        keycloakUser: action.payload
      };
    },
    purgeAuth(state) {
      state.isAuthenticated = false;
      state.keycloakUser = null;
      state.keycloakReady = false;
      state.matrixCredentials = null;
      state.userBlacklist = [];
      state.locationSet = false;
      state.stripeAccountVerified = false;
      state.stripeAccountRequirements = null;
      state.appTag = null;
      state.isUserHealthManager = false;
      state.currentCompanyId = null;
      state.isPublicCompany = false;
      state.currentEvent = null;
      state.currentGroup = null;
      state.currentOnDemandEvent = null;
      state.paymentFeeData = null;
      state.uses19Vat = null;
      state.companySportsLoaded = false;
    },
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    setKeycloakReady(state, action: PayloadAction<boolean>) {
      state.keycloakReady = action.payload;
    },
    updateToken(state, action: PayloadAction<string>) {
      if (!state.keycloakUser) {
        return state;
      }
      return {
        ...state,
        keycloakUser: {
          ...state.keycloakUser,
          token: action.payload
        }
      };
    },
    setMatrixCredentials(state, action: PayloadAction<MatrixCredentials>) {
      state.matrixCredentials = action.payload;
    },
    setUserBlacklist(state, action: PayloadAction<string[]>) {
      state.userBlacklist = action.payload;
    },
    setNotificationPermission(
      state,
      action: PayloadAction<NotificationPermission>
    ) {
      state.notificationPermission = action.payload;
    },
    setStartLocations(state) {
      state.locationSet = true;
    },
    setStripeAccountVerification(
      state,
      action: PayloadAction<{
        isVerified: boolean;
        requirements: {
          currently_due: string[];
          pending_verification: string[];
          past_due: string[];
        };
      }>
    ) {
      return {
        ...state,
        stripeAccountVerified: action.payload.isVerified,
        stripeAccountRequirements: action.payload.requirements
      };
    },
    setAppTag(state, action: PayloadAction<string>) {
      state.appTag = action.payload;
    },
    purgeAppTag(state) {
      state.appTag = null;
    },
    setCurrentTab(state, action: PayloadAction<number>) {
      state.currentTab = action.payload;
    },
    setIsUserHealthManager(state) {
      state.isUserHealthManager = true;
    },
    setCurrentCompanyId(state, action: PayloadAction<string | null>) {
      state.currentCompanyId = action.payload;
    },
    setIsPublicCompany(state, action: PayloadAction<boolean>) {
      state.isPublicCompany = action.payload;
    },
    setCurrentEvent(state, action: PayloadAction<string>) {
      state.currentEvent = action.payload;
    },
    resetCurrentEvent(state) {
      state.currentEvent = null;
    },
    setCurrentOnDemandEvent(state, action: PayloadAction<string>) {
      state.currentOnDemandEvent = action.payload;
    },
    resetCurrentOnDemandEvent(state) {
      state.currentOnDemandEvent = null;
    },
    setCurrentGroup(state, action: PayloadAction<string>) {
      state.currentGroup = action.payload;
    },
    resetCurrentGroup(state) {
      state.currentGroup = null;
    },
    setPaymentFeeData(state, action: PayloadAction<PaymentFeeData>) {
      state.paymentFeeData = action.payload;
    },
    setUses19Vat(state, action: PayloadAction<boolean>) {
      state.uses19Vat = action.payload;
    },
    setCompanySportsLoaded(state, action: PayloadAction<boolean>) {
      state.companySportsLoaded = action.payload;
    }
  }
});

export const {
  startLoading,
  stopLoading,
  setKeycloakReady,
  loginUser,
  purgeAuth,
  updateToken,
  setMatrixCredentials,
  setUserBlacklist,
  setNotificationPermission,
  setStartLocations,
  setStripeAccountVerification,
  setAppTag,
  purgeAppTag,
  setCurrentTab,
  setIsUserHealthManager,
  setCurrentCompanyId,
  setIsPublicCompany,
  setCurrentEvent,
  resetCurrentEvent,
  setCurrentOnDemandEvent,
  resetCurrentOnDemandEvent,
  setCurrentGroup,
  resetCurrentGroup,
  setPaymentFeeData,
  setUses19Vat,
  setCompanySportsLoaded
} = authSlice.actions;

export default authSlice.reducer;
